'use client'

import { useEffect } from 'react'

import { getNavigationItems } from '@common/core/config/navigation'

import { PORTAL_TUTORIAL_NAVBAR_TARGET_BY_URL } from '../const/portal-tutorial-navbar-target-by-url.const'
import { PORTAL_TUTORIAL_SIDE_NAV_TARGET_BY_ID } from '../const/portal-tutorial-side-nav-target-by-id.const'
import type { PortalTutorialTargetAnnotationsOptions } from '../interfaces/portal-tutorial-target-annotations-options.interface'

function annotateSideNavigation(container: HTMLElement) {
  const navigationItems = getNavigationItems()
  const links = Array.from(container.querySelectorAll<HTMLElement>('a, button'))

  navigationItems.forEach((item, index) => {
    const targetId = PORTAL_TUTORIAL_SIDE_NAV_TARGET_BY_ID[item.id]
    const element = links[index]
    if (!targetId || !element) return
    element.setAttribute('data-portal-tutorial', targetId)
  })
}

function annotateNavbarItems(container: HTMLElement) {
  const links = Array.from(container.querySelectorAll<HTMLElement>('a[href]'))

  links.forEach((link) => {
    const href = link.getAttribute('href')
    if (!href) return

    const url = new URL(href, window.location.origin)
    const segments = url.pathname.split('/').filter(Boolean)
    const route = segments.length >= 2 ? segments[segments.length - 1] : segments[0]
    const targetId = route ? PORTAL_TUTORIAL_NAVBAR_TARGET_BY_URL[route] : undefined

    if (targetId) {
      link.setAttribute('data-portal-tutorial', targetId)
    }
  })
}

export function usePortalTutorialTargetAnnotations({
  sideNavContainerRef,
  navbarContainerRef,
  enabled = true,
}: PortalTutorialTargetAnnotationsOptions) {
  useEffect(() => {
    if (!enabled) return

    const annotate = () => {
      if (sideNavContainerRef.current) {
        annotateSideNavigation(sideNavContainerRef.current)
      }

      if (navbarContainerRef.current) {
        annotateNavbarItems(navbarContainerRef.current)
      }
    }

    annotate()

    const sideNavObserver =
      sideNavContainerRef.current && typeof MutationObserver !== 'undefined'
        ? new MutationObserver(() => annotate())
        : null

    const navbarObserver =
      navbarContainerRef.current && typeof MutationObserver !== 'undefined'
        ? new MutationObserver(() => annotate())
        : null

    sideNavObserver?.observe(sideNavContainerRef.current!, { childList: true, subtree: true })
    navbarObserver?.observe(navbarContainerRef.current!, { childList: true, subtree: true })

    return () => {
      sideNavObserver?.disconnect()
      navbarObserver?.disconnect()
    }
  }, [enabled, navbarContainerRef, sideNavContainerRef])
}
